// DRAFT — not legal advice. Every factual assertion here must be confirmed by LAA and reviewed by counsel before this site accepts public traffic or runs paid advertising.
import { createFileRoute, Link } from "@tanstack/react-router";

import { seo } from "@/lib/seo";
import { NAP, SHOW_PHONE } from "@/lib/business";
import { LegalPage, LegalSection } from "@/components/legal/LegalPage";

export const Route = createFileRoute("/_site/legal/privacy")({
  head: () =>
    seo({
      title: "Privacy Policy",
      description:
        "What LAA Concierge Consulting collects through this site, why, who it is shared with, how long it is kept, and how to exercise your rights.",
      path: "/legal/privacy",
    }),
  component: Page,
});

function Page() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      lastUpdated="4 September 2026"
      intro="This policy explains what LAA Concierge Consulting, LLC collects when you use this website, why we collect it, who else sees it, and what you can ask us to do about it."
    >
      <LegalSection title="Who this policy covers">
        <p>
          This policy applies to {NAP.legalName} ("LAA", "we", "us") and to the website at
          laaconsulting.com, including its contact form, booking request form, and the AI
          Opportunity Scorecard tool. It applies to visitors to the site. Information you give us
          during a paid engagement is additionally governed by the written scope agreement for that
          engagement.
        </p>
      </LegalSection>

      <LegalSection title="What we collect">
        <p>We collect three kinds of information.</p>
        <p>
          <strong>Information you type in.</strong> Our contact form and booking request form ask
          for first name, last name, email address, phone number, company name, the service you are
          interested in, your message, and — for booking requests — the days and times that suit
          you. Email address is the only required field. We also record whether you ticked the box
          consenting to be contacted. If you answer the three optional questions shown after you
          submit a form, we store your answers about company size, what is driving the enquiry, and
          your intended timeline.
        </p>
        <p>
          <strong>Marketing attribution data.</strong> When you arrive at the site we record the
          campaign parameters present in the URL — <code>utm_source</code>, <code>utm_medium</code>,{" "}
          <code>utm_campaign</code>, <code>utm_term</code>, <code>utm_content</code> — and the click
          identifiers set by advertising platforms: <code>gclid</code> (Google), <code>fbclid</code>{" "}
          (Meta), <code>ttclid</code> (TikTok), and <code>msclkid</code> (Microsoft). We also record
          the page you landed on, the referring website if your browser sends one, the page you were
          on when you submitted a form, and your browser's user agent string. This information is
          held in your browser's local storage until you submit a form, at which point it is saved
          alongside your enquiry. First touch wins: if you visit more than once, we keep the details
          of the first visit.
        </p>
        <p>
          <strong>Analytics data.</strong> Once website analytics are installed, we will collect
          aggregate usage data such as pages viewed, approximate location derived from IP address,
          device and browser type, and how you moved through the site. We will update this policy
          and name the analytics provider before that collection begins.
        </p>
        <p>
          We do not ask for and do not want sensitive personal information — government
          identification numbers, financial account numbers, health information, or biometric data —
          through this website. Please do not put such information in a form message. Where a
          funding engagement genuinely requires financial documents, we will collect them through a
          separate secure channel agreed with you.
        </p>
      </LegalSection>

      <LegalSection title="Why we collect it">
        <ul>
          <li>To reply to your enquiry and arrange a call.</li>
          <li>
            To deliver the consulting services you ask us for, and to administer the engagement,
            including invoicing.
          </li>
          <li>
            To understand which marketing channels bring us enquiries, so that we spend money on the
            ones that work. This is the sole purpose of the attribution data described above.
          </li>
          <li>
            To keep records we are required to keep, to enforce our terms, and to defend legal
            claims.
          </li>
          <li>
            To send you marketing email about our services, only where you ticked the consent box.
            Every marketing email carries an unsubscribe link.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Legal bases">
        <p>
          Where a legal basis is required — for example for visitors in the United Kingdom or the
          European Economic Area — we rely on the following:
        </p>
        <ul>
          <li>
            <strong>Consent</strong> for marketing email and for non-essential cookies and
            analytics. You can withdraw consent at any time.
          </li>
          <li>
            <strong>
              Performance of a contract, or steps taken at your request before entering one
            </strong>{" "}
            for responding to enquiries, scheduling calls, and delivering services.
          </li>
          <li>
            <strong>Legitimate interests</strong> for measuring marketing effectiveness, securing
            the site, and preventing abuse of our forms. We have considered these interests against
            your rights and use the minimum data needed.
          </li>
          <li>
            <strong>Legal obligation</strong> for tax, accounting, and record-keeping requirements.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Who we share it with">
        <p>
          <strong>
            We do not sell your personal information, and we do not share it for cross-context
            behavioural advertising.
          </strong>{" "}
          We disclose it only to service providers who process it on our instructions, and only so
          far as they need it:
        </p>
        <ul>
          <li>
            <strong>Hosting and database infrastructure</strong> — the vendors who run this website
            and store form submissions.
          </li>
          <li>
            <strong>Email delivery</strong> — the provider that carries our replies and, where you
            have consented, our marketing email.
          </li>
          <li>
            <strong>Advertising and analytics platforms</strong>, where applicable. If we send
            conversion data back to an advertising platform to measure campaign performance, that
            transfer will be limited to what the platform requires and will be disclosed here by
            name before it begins.
          </li>
          <li>
            <strong>Professional advisers</strong> such as our accountants and lawyers, and any
            party we are legally compelled to disclose to.
          </li>
        </ul>
        <p>
          If our business is sold or merged, enquiry records may transfer to the acquirer as part of
          that transaction. We would tell you before your information became subject to a materially
          different policy.
        </p>
      </LegalSection>

      <LegalSection title="How long we keep it">
        <p>
          Enquiries that do not become engagements are kept for up to 24 months from the last
          contact, so that we can recognise you if you come back and so we can measure campaign
          performance over a realistic sales cycle. Records relating to a paid engagement are kept
          for seven years after the engagement ends, to meet tax, accounting, and professional
          record-keeping requirements. Marketing consent records are kept for as long as we rely on
          them, plus the period in which a complaint could be brought. Where we no longer need
          identifying details but still want the aggregate marketing numbers, we delete the personal
          fields and keep only the counts.
        </p>
      </LegalSection>

      <LegalSection title="How we protect it">
        <p>
          The site is served over HTTPS. Form submissions are written to a managed database that is
          encrypted at rest, and access rules on that database are configured so that the public
          website can submit an enquiry but cannot read enquiries back. Administrative access is
          limited to people at LAA who need it and is protected by individual accounts. No system is
          perfectly secure, and we cannot guarantee that a determined attacker will never succeed;
          if a breach affects your information we will notify you and the relevant authorities where
          the law requires it.
        </p>
      </LegalSection>

      <LegalSection title="Your rights and how to use them">
        <p>
          Depending on where you live, you may have the right to ask us to give you a copy of the
          personal information we hold about you, correct it if it is wrong, delete it, limit how we
          use it, provide it in a portable format, or stop using it for direct marketing. You may
          also withdraw consent you previously gave, without affecting anything we did before you
          withdrew it.
        </p>
        <p>
          To exercise any of these, email <a href={`mailto:${NAP.email}`}>{NAP.email}</a>
          {SHOW_PHONE ? (
            <>
              {" or call "}
              <a href={NAP.phoneHref}>{NAP.phone}</a>
            </>
          ) : null}
          . We will acknowledge your request within ten business days and respond substantively
          within 45 days, and we will tell you if we need longer. We may ask you to confirm your
          identity — usually by replying from the email address the enquiry came from — before we
          act on a request about specific records. We will not treat you differently for making a
          request.
        </p>
      </LegalSection>

      <LegalSection title="Nevada residents (NRS 603A)">
        <p>
          LAA operates this website from Nevada, and Nevada's online privacy law, NRS Chapter 603A,
          applies to us as a website operator. Under NRS 603A.340, a Nevada consumer may direct an
          operator not to make any sale of covered information the operator has collected or will
          collect about them.
        </p>
        <p>
          We do not sell covered information as that term is defined in NRS 603A.300 — that is, we
          do not exchange it for money with a third party who will license or sell it on. You may
          nonetheless submit an opt-out request, and we will honour it. Send the request to{" "}
          <a href={`mailto:${NAP.email}`}>{NAP.email}</a> with the subject line "Nevada opt-out", or
          by post to {NAP.legalName}, {NAP.street}, {NAP.city}, {NAP.region} {NAP.postalCode}.
          Include the email address and, if applicable, the phone number you used on the site so we
          can find the record. We will verify and respond within 60 days, and may extend by a
          further 30 days where reasonably necessary, in which case we will tell you.
        </p>
      </LegalSection>

      <LegalSection title="California residents">
        <p>
          The California Consumer Privacy Act, as amended by the California Privacy Rights Act,
          applies only to businesses that meet statutory thresholds. In broad terms, a business is
          covered if it has annual gross revenue above roughly $25 million, buys, sells, or shares
          the personal information of 100,000 or more California consumers or households in a year,
          or derives 50 percent or more of its annual revenue from selling or sharing personal
          information.
        </p>
        <p>
          LAA does not currently believe it meets any of these thresholds, and so does not believe
          it is a covered business under the CCPA/CPRA. That is our present assessment rather than a
          determination by any regulator, and the thresholds are indexed and periodically adjusted,
          so it may change. We do not claim exemption as a settled matter.
        </p>
        <p>
          Regardless of whether the statute obliges us, we will honour requests from California
          residents to know, delete, correct, or opt out of sale or sharing on the same terms we
          offer everyone else, and we will not discriminate against anyone who makes one. Use the
          contact details in the section above. We do not knowingly sell or share the personal
          information of consumers under 16.
        </p>
      </LegalSection>

      <LegalSection title="Cookies and similar technologies">
        <p>
          We use a small number of strictly necessary technologies to make the site work — for
          example, browser local storage that holds the campaign parameters described above until
          you submit a form. These are essential to the service you asked for.
        </p>
        <p>
          Any non-essential cookies or similar technologies, including analytics and advertising
          tags, are governed by the consent banner. They are not loaded until you accept them, and
          you can change or withdraw your choice at any time through the banner's controls. Your
          browser settings also let you block or delete cookies, though doing so may affect how
          parts of the site behave.
        </p>
        <p>
          We do not currently respond to browser Do Not Track signals, because there is no common
          standard for how a site should interpret them. We do treat a Global Privacy Control signal
          as an opt-out request where the law recognises it.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p>
          Our services are directed at businesses, not at individuals under 18, and we do not
          knowingly collect personal information from anyone under 18. If you believe a child has
          given us information, email <a href={`mailto:${NAP.email}`}>{NAP.email}</a> and we will
          delete it.
        </p>
      </LegalSection>

      <LegalSection title="Changes to this policy">
        <p>
          When we change this policy we update the "Last updated" date at the top of this page. If a
          change materially affects how we use information we already hold — a new category of
          recipient, a new purpose, a longer retention period — we will post a notice on the site
          before the change takes effect, and where we hold your email address and the law requires
          it, we will tell you directly and, if consent is needed, ask for it again.
        </p>
        <p>
          Questions about this policy go to <a href={`mailto:${NAP.email}`}>{NAP.email}</a>. See
          also our <Link to="/legal/terms">Terms of Service</Link> and{" "}
          <Link to="/legal/disclaimer">Disclaimer</Link>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
